import { useEffect, useRef, useCallback } from 'react';
import { useAutoFocusContextSafe } from './useAutoFocusContextSafe';

/**
 * Custom hook for auto-focusing input elements after a period of user inactivity
 * @param {Object} options - Configuration options
 * @param {number} options.timeout - Timeout in milliseconds (default: 10000ms = 10 seconds)
 * @param {boolean} options.enabled - Whether auto-focus is enabled (default: true)
 * @param {Array} options.excludeEvents - Array of event types to exclude from resetting the timer
 * @param {Function} options.onFocus - Callback function called when auto-focus is triggered
 * @param {string} options.componentId - Unique identifier for this component (optional)
 * @returns {Object} - Object containing inputRef and manual focus function
 */
export const useAutoFocus = ({
	timeout = 10000, // 10 seconds default
	enabled = true,
	excludeEvents = ['mousemove', 'mouseover', 'mouseenter', 'mouseleave'],
	onFocus = null,
	componentId = null, // Optional component ID for global context
} = {}) => {
	const inputRef = useRef(null);
	const timeoutRef = useRef(null);
	const lastActivityRef = useRef(Date.now());
	
	// Get context safely
	const context = useAutoFocusContextSafe();

	// Function to manually trigger focus
	const focusInput = useCallback(() => {
		if (inputRef.current && typeof inputRef.current.focus === 'function') {
			inputRef.current.focus();
			if (onFocus) {
				onFocus();
			}
		}
	}, [onFocus]);

	// Function to reset the inactivity timer
	const resetTimer = useCallback(() => {
		if (!enabled) return;
		
		lastActivityRef.current = Date.now();
		
		// Clear existing timeout
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		
		// Set new timeout
		timeoutRef.current = setTimeout(() => {
			focusInput();
		}, timeout);
	}, [enabled, timeout, focusInput]);

	// Function to handle user activity
	const handleUserActivity = useCallback((event) => {
		// Skip if auto-focus is disabled
		if (!enabled) return;
		
		// Skip excluded events
		if (excludeEvents.includes(event.type)) return;
		
		// Reset the timer on any user activity
		resetTimer();
	}, [enabled, excludeEvents, resetTimer]);

	// Register/unregister with global context
	useEffect(() => {
		if (context && componentId) {
			context.registerInput(componentId, inputRef);
			
			return () => {
				context.unregisterInput(componentId);
			};
		}
	}, [context, componentId]);

	// Set up event listeners for user activity
	useEffect(() => {
		if (!enabled) return;

		// List of events to listen for user activity
		const activityEvents = [
			'click',
			'keydown',
			'keyup',
			'keypress',
			'scroll',
			'touchstart',
			'touchend',
			'touchmove',
			'focus',
			'blur',
			'input',
			'change',
			'submit',
		];

		// Add event listeners
		activityEvents.forEach(eventType => {
			document.addEventListener(eventType, handleUserActivity, true);
		});

		// Start the initial timer
		resetTimer();

		// Cleanup function
		return () => {
			// Remove event listeners
			activityEvents.forEach(eventType => {
				document.removeEventListener(eventType, handleUserActivity, true);
			});
			
			// Clear timeout
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [enabled, handleUserActivity, resetTimer]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	return {
		inputRef,
		focusInput,
		resetTimer,
		// Expose context methods if available
		focusNearestInput: context?.focusNearestInput,
		focusInputById: context?.focusInputById,
	};
};

export default useAutoFocus;
